
import axios from "axios";
import ENDPOINTS from "./endpoints";
import { AUTH_TOKEN_KEY } from "./Axios";

// The bulk-upload endpoints live in a separate service (not app-service-api.muradiz.com).
// Set REACT_APP_BULK_API_URL in .env to point at it; it defaults to the local service.
export const BULK_API_URL =
  process.env.REACT_APP_BULK_API_URL || "http://localhost:4000/api";

const BULK_UPLOAD_ENDPOINTS = ENDPOINTS.bulkUpload;

// Excel upload / template download can take longer than a normal request.
const LONG_TIMEOUT = 120000;

const Axios = axios.create({
  baseURL: BULK_API_URL,
  timeout: LONG_TIMEOUT,
});

Axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  config.headers = config.headers || {};
  if (token) {
    config.headers["Authorization"] = `Bearer ${token.replace(/^Bearer\s+/i, "").trim()}`;
  }
  config.headers["x-country"] = localStorage.getItem("muradi_country") || "PK";
  config.headers["x-language"] = localStorage.getItem("muradi_language") || "en";
  return config;
});

Axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const body = error?.response?.data;
    const serverMessage =
      body?.error?.message || body?.message || (typeof body?.error === "string" ? body.error : undefined);
    if (!error?.response) {
      error.friendlyMessage = `Could not reach the bulk upload service at ${BULK_API_URL}. Make sure it is running.`;
    } else if (serverMessage) {
      error.friendlyMessage = serverMessage;
    }
    return Promise.reject(error);
  }
);

const unwrap = (value: any): any =>
  value?.data?.data ?? value?.data ?? value;

// Bulk upload service
const BulkUploadService = {
  // 1. Get categories
  async getCategories() {
    const res = await Axios.get(
      BULK_UPLOAD_ENDPOINTS.categories
    );

    const data = unwrap(res);

    return Array.isArray(data)
      ? data
      : data?.items || data?.content || [];
  },

  // 2. Download Excel template
  async downloadTemplate(categoryIds: number[] = []) {
    const params = categoryIds.length
      ? { categories: categoryIds.join(",") }
      : {};

    return Axios.get(
      BULK_UPLOAD_ENDPOINTS.template,
      {
        params,
        responseType: "blob",
        timeout: LONG_TIMEOUT,
      }
    );
  },

  // 3. Upload Excel file
  async upload(file: File) {
    const form = new FormData();
    form.append("file", file);

    const res = await Axios.post(
      BULK_UPLOAD_ENDPOINTS.upload,
      form,
      { timeout: LONG_TIMEOUT }
    );

    return unwrap(res);
  },

  // 4. Get validation/review data
  async getReview(batchId: string) {
    const res = await Axios.get(
      BULK_UPLOAD_ENDPOINTS.review,
      {
        params: { batchId },
      }
    );

    return unwrap(res);
  },

  // 5. Confirm selected products
  async confirm(
    batchId: string,
    productIds: Array<number | string>
  ) {
    const res = await Axios.post(
      BULK_UPLOAD_ENDPOINTS.confirm,
      {
        batchId,
        productIds,
      }
    );

    return unwrap(res);
  },

  // 6. Get final review
  async getFinal(batchId: string) {
    const res = await Axios.get(
      BULK_UPLOAD_ENDPOINTS.final,
      {
        params: { batchId },
      }
    );

    return unwrap(res);
  },

  // 7. Publish/finalize products
  async finalize(batchId: string) {
    const res = await Axios.post(
      BULK_UPLOAD_ENDPOINTS.finalize,
      { batchId }
    );

    return unwrap(res);
  },
};

export default BulkUploadService;
